import React, { useState } from 'react';
import { Users, Zap, ShieldCheck, Sparkles, CalendarDays, Repeat2, Share2, Copy, Check } from 'lucide-react';
import { useCommunityMetrics } from '../hooks/useCommunityMetrics';
import './MyCommunityPage.css';

/**
 * "Your Community" dashboard (/my-community) — D9 + Web UI section of
 * referral-leaderboard-program.md. Rendered behind RequireAuth in App.tsx.
 *
 * Design: a "community passport" — the score sits in a wax-seal-style stamp
 * (this is an ambassador program: real people you personally brought into
 * real, in-person meetups), and the two stat lines from the contract's tile
 * layout sit on a ticket-stub card below it, divided by a perforation — the
 * same "you were there" idea as the PIN check-in this program is built to
 * reward. Continues this app's existing indigo/violet identity (established
 * in AuthLoginPage.tsx) rather than introducing an unrelated new palette.
 */

const STAT_ROW_ONE = (community: NonNullable<ReturnType<typeof useCommunityMetrics>['community']>) => [
  { icon: Users, value: community.members, label: 'Members' },
  { icon: Zap, value: community.active, label: 'Active' },
  { icon: ShieldCheck, value: community.verified_attendees, label: 'Verified Attendees' },
  { icon: Sparkles, value: community.new_this_week, label: 'New This Week' },
];

const STAT_ROW_TWO = (community: NonNullable<ReturnType<typeof useCommunityMetrics>['community']>) => [
  { icon: CalendarDays, value: community.events, label: 'Events' },
  { icon: Repeat2, value: community.recurring_members, label: 'Recurring Members' },
  { icon: Share2, value: community.active_referrers, label: 'Active Referrers' },
];

const MyCommunityPage: React.FC = () => {
  const { isLoading, error, myReferrals, community } = useCommunityMetrics();
  const [copied, setCopied] = useState(false);

  const handleCopyCode = () => {
    if (!myReferrals?.referral_code) return;
    navigator.clipboard.writeText(myReferrals.share_url || myReferrals.referral_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (isLoading) {
    return (
      <div className="mc-root">
        <div className="mc-skeleton" aria-busy="true" aria-live="polite">
          <span className="mc-skeleton-spinner" />
          Loading your community…
        </div>
      </div>
    );
  }

  if (error || !community || !myReferrals) {
    return (
      <div className="mc-root">
        <div className="mc-error" role="alert">
          <p>We couldn't load your community right now.</p>
          <p className="mc-error-detail">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mc-root">
      <div className="mc-passport">
        <div className="mc-passport-band">
          <span className="mc-eyebrow">Your Community</span>
          <h1 className="mc-headline">The people you brought here, verified.</h1>
        </div>

        <div className="mc-passport-body">
          <div className="mc-stamp">
            <div className="mc-stamp-ring">
              <span className="mc-stamp-score">{community.community_score}</span>
              <span className="mc-stamp-label">Community<br />Score</span>
            </div>
          </div>

          <div className="mc-stats">
            <div className="mc-stat-row">
              {STAT_ROW_ONE(community).map(({ icon: Icon, value, label }) => (
                <div className="mc-stat" key={label}>
                  <Icon size={18} className="mc-stat-icon" />
                  <span className="mc-stat-value">{value}</span>
                  <span className="mc-stat-label">{label}</span>
                </div>
              ))}
            </div>

            <div className="mc-perforation" role="presentation" />

            <div className="mc-stat-row">
              {STAT_ROW_TWO(community).map(({ icon: Icon, value, label }) => (
                <div className="mc-stat" key={label}>
                  <Icon size={18} className="mc-stat-icon" />
                  <span className="mc-stat-value">{value}</span>
                  <span className="mc-stat-label">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mc-secondary">
        <div className="mc-code-card">
          <span className="mc-code-eyebrow">Your referral code</span>
          {myReferrals.referral_code ? (
            <>
              <div className="mc-code-row">
                <span className="mc-code">{myReferrals.referral_code}</span>
                <button className="mc-copy-btn" onClick={handleCopyCode} aria-label="Copy share link">
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied' : 'Copy link'}
                </button>
              </div>
              <p className="mc-code-hint">{myReferrals.share_url}</p>
            </>
          ) : (
            <p className="mc-code-hint">You don't have a referral code yet — ask an admin to make you an ambassador.</p>
          )}
        </div>

        {myReferrals.badges.length > 0 && (
          <div className="mc-badges-card">
            <span className="mc-code-eyebrow">Badges</span>
            <div className="mc-badges-row">
              {myReferrals.badges.map((badge) => (
                <div className="mc-badge" key={badge.code} title={badge.name}>
                  <span className="mc-badge-icon">{badge.icon}</span>
                  <span className="mc-badge-name">{badge.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCommunityPage;
